import { CreateCompanyNotePayload, Note, UpdateCrmCompanyNotePayload } from "types/crm/notes";
import { usePaginatedList } from "../usePaginatedList";
import { companyApi, companyQueryKeys } from "service/crm/api/company";
import { useEntityCreateMutation } from "../useEntityMutation";
import { useMutation, useQueryClient } from "@tanstack/react-query";


export const useCompanyNotes = (companyUuid: string, pageSize = 20) => {
  const { items, ...rest } = usePaginatedList<Note>(
    companyQueryKeys.notes(companyUuid),
    (page) => companyApi.getCompanyNotes(companyUuid, page, pageSize),
    !!companyUuid,
  );
  return { notes: items, ...rest };
};

export const useCreateCompanyNoteMutation = () =>
  useEntityCreateMutation<CreateCompanyNotePayload, Note>(
    (payload) => companyApi.addCompanyNote(payload),
    (variables) => [
      companyQueryKeys.notes(variables.companyUuid),
      companyQueryKeys.activities(variables.companyUuid),
    ],
  );

export const useUpdateCompanyNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCrmCompanyNotePayload) =>
      companyApi.updateCompanyNote(payload),
    onSuccess: (_data, variables) => {
      // Prefix-match invalidation: clears the cache for every status filter
      // of this contact's task list, not just "ALL".
      queryClient.invalidateQueries({
        queryKey: [...companyQueryKeys.all, "notes", variables.companyUuid],
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};

export const useDeleteCompanyNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyUuid,
      noteUuid,
    }: {
      companyUuid: string;
      noteUuid: string;
    }) => companyApi.deleteCompanyNote(companyUuid, noteUuid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...companyQueryKeys.all, "notes", variables.companyUuid],
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};